import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQuery } from "./baseQuery";
import { Id } from "./id";
import { Page } from "./page";
import { Article, ArticleForm, ArticleQuery, ArticleUpdateStatusForm, 
         DeliveryOrganizationForm, DeliveryOrganizationQuery } from "./article";
import { User, UserForm, UserQuery } from "./user";
import { ScheduleQuery, ScheduleForm } from "./schedule";

interface UploadFileChunkRequest {
    upload_id: string;
    article_id?: number;
    file_name: string;
    size: number;
    no: number;
    total_chunks: number;
    chunk_size: number;
    base64_data: string;
}

interface UploadFileChunkResponse {
    upload_id: string;
    uploaded_chunks: number;
    total_chunks: number;
    uploaded_size: number;
    progress: number;
}

/* file_serverを特定する必要があるためorganization_idを追加 */
interface CompleteUploadFileChunkRequest {
    upload_id: string;
    organization_id?: number;
}

interface CancelUploadFileChunkRequest {
    upload_id: string;
}

interface CompleteUploadFileChunkResponse {
    upload_id: string;
    id: number;
    uri: string;
    size: number;
}
export const articleApi = createApi({
    reducerPath: "articleApi",
    baseQuery,
    tagTypes: ['Article', 'User'],
    endpoints: (builder) => ({
        getArticles: builder.query<Page<Article>, ArticleQuery>({
            query: (params) => ({
                url: "articles/manage/list",
                params,
            }),
            providesTags: ['Article','User'],
        }),
        getArticle: builder.query<Article, Id>({
            query: (id) => `articles/manage/${id}`,
            providesTags: ['Article'],
        }),
        getArticleEdit: builder.query<Article, Id>({
            query: (id) => `articles/manage/edit/${id}`,
            providesTags: ['Article'],
        }),
        getDeliveryOrganizations: builder.query<DeliveryOrganizationForm[], DeliveryOrganizationQuery>({
            query: (params) => ({
                url: `deliverygroups/organizationlist`,
                params,
            }),
            providesTags: ['Article'],
        }),
        getDailySchedule: builder.query<Article[], ScheduleQuery>({
            query: (params) => ({
                url: `schedule/daily`,
                params,
            }),
            providesTags: ['Article'],
        }),
        getMonthlySchedule: builder.query<Article[], ScheduleQuery>({
            query: (params) => ({
                url: `schedule/monthly`,
                params,
            }),
            providesTags: ['Article'],
        }),
        getUsers: builder.query<Page<User>, UserQuery | void>({
            query: (params) => {
                return { url: "users/list", params: params || undefined };
            },
            providesTags: ['Article','User'],
        }),
        getUser: builder.query<User, Id>({
            query: (id) => ({ url: `users/${id}` }),
            providesTags: ['Article','User'],
        }),
        getMyUser: builder.query<User, void>({
            query: () => ({ url: "users/me" }),
            providesTags: ['Article','User'],
        }),
        createArticle: builder.mutation<void, ArticleForm>({
            query: (body) => {
                return {
                    url: `articles/manage`,
                    method: "post",
                    body,
                };
            },
            invalidatesTags: ['Article'],
        }),
        uploadFileChunk: builder.mutation<UploadFileChunkResponse, UploadFileChunkRequest>({
            query: (body) => ({
                url: `articles/manage/files/chunks`,
                method: "post",
                body,
            }),
        }),
        completeUploadFileChunk: builder.mutation<CompleteUploadFileChunkResponse, CompleteUploadFileChunkRequest>({
            query: (body) => ({
                url: `articles/manage/files/chunks/complete`,
                method: "post",
                body,
            }),
        }),
        cancelUploadFileChunk: builder.mutation<void, CancelUploadFileChunkRequest>({
            query: (body) => ({
                url: `articles/manage/files/chunks/cancel`,
                method: "post",
                body,
            }),
        }),
        updateArticle: builder.mutation<void, ArticleForm>({
            query: ({ id, ...body }) => ({
                url: `articles/manage/${id}`,
                method: "post",
                body,
            }),
            invalidatesTags: ['Article'],
        }),
        bulkDeleteArticles: builder.mutation<void, number[]>({
            query: (articleIds) => ({
                url: `articles/manage`,
                method: "delete",
                body: {
                    articleIds,
                },
            }),
            invalidatesTags: ['Article'],
        }),
        bulkUpdateArticleStatus: builder.mutation<void, ArticleUpdateStatusForm>({
            query: (body) => ({
                url: "articles/manage/status",
                method: "post",
                body,
            }),
            invalidatesTags: ['Article'],
        }),
        updateDailySchedule: builder.mutation<void, ScheduleForm>({
            query: ({ deliveryGroupId, body }) => ({
                url: `schedule/daily/${deliveryGroupId}`,
                method: "post",
                body,
            }),
            invalidatesTags: ['Article'],
        }),
        updateMonthlySchedule: builder.mutation<void, ScheduleForm>({
            query: ({ deliveryGroupId, body }) => ({
                url: `schedule/monthly/${deliveryGroupId}`,
                method: "post",
                body,
            }),
            invalidatesTags: ['Article'],
        }),
        updateUser: builder.mutation<void, UserForm>({
                    query: ({ id, ...body }) => ({
                        url: `users/edit/${id}`,
                        method: "post",
                        body,
                    }),
            invalidatesTags: ['Article','User'],
        }),
        deleteUser: builder.mutation<void, Id>({
            query: (id) => ({ url: `users/${id}`, method: "delete" }),
            invalidatesTags: ['User'],
        }),
        enableUser: builder.mutation<void, Id>({
            query: (id) => ({ url: `users/enable/${id}`, method: "post" }),
            invalidatesTags: ['User'],
        }),
        disableUser: builder.mutation<void, Id>({
            query: (id) => ({ url: `users/disable/${id}`, method: "post" }),
            invalidatesTags: ['User'],
        }),
    }),
});

export const {
    useGetArticlesQuery,
    useGetArticleQuery,
    useGetArticleEditQuery,
    useGetDeliveryOrganizationsQuery,
    useGetDailyScheduleQuery,
    useGetMonthlyScheduleQuery,
    useGetUsersQuery,
    useGetUserQuery,
    useGetMyUserQuery,
    useCreateArticleMutation,
    useUploadFileChunkMutation,
    useCompleteUploadFileChunkMutation,
    useCancelUploadFileChunkMutation,
    useUpdateArticleMutation,
    useBulkDeleteArticlesMutation,
    useBulkUpdateArticleStatusMutation,
    useUpdateDailyScheduleMutation,
    useUpdateMonthlyScheduleMutation,
    useUpdateUserMutation,
    useDeleteUserMutation,
    useEnableUserMutation,
    useDisableUserMutation,
} = articleApi;
